import React, { useState, useEffect } from 'react';
import { Brain, Upload, Zap, DollarSign, Loader2, AlertCircle, CheckCircle, FileText, X, Sparkles, Star } from 'lucide-react';

// Sample optimized kernel for Mandeep Singh (always available for testing)
const SAMPLE_KERNEL = {
  "person": {
    "name": "Mandeep Singh",
    "date": "1997-05-14",
    "time": "12:17:00",
    "place": "Dehradun, Uttarakhand, India",
    "latitude": 30.3255646,
    "longitude": 78.0436813,
    "timezone": "Asia/Kolkata"
  },
  "d1_chart": {
    "lagna": {"sign": "Leo", "degree": 3.2584, "nakshatra": "Magha", "pada": 1, "lord": "Sun"},
    "sun": {"sign": "Aries", "house": 9, "degree": 29.699, "nakshatra": "Krittika", "pada": 1, "status": "Exalted"},
    "moon": {"sign": "Cancer", "house": 12, "degree": 27.5, "nakshatra": "Ashlesha", "pada": 4, "status": "Own"},
    "mars": {"sign": "Leo", "house": 1, "degree": 24.3, "nakshatra": "Purva Phalguni", "pada": 4},
    "mercury": {"sign": "Aries", "house": 9, "degree": 20.8, "nakshatra": "Bharani", "pada": 4},
    "jupiter": {"sign": "Capricorn", "house": 6, "degree": 27.2, "nakshatra": "Dhanishta", "pada": 2, "status": "Debilitated"},
    "venus": {"sign": "Taurus", "house": 10, "degree": 15.6, "nakshatra": "Rohini", "pada": 3, "status": "Own"},
    "saturn": {"sign": "Pisces", "house": 8, "degree": 21.4, "nakshatra": "Revati", "pada": 1},
    "rahu": {"sign": "Virgo", "house": 2, "degree": 3.1, "nakshatra": "Uttara Phalguni", "pada": 2},
    "ketu": {"sign": "Pisces", "house": 8, "degree": 3.1, "nakshatra": "Purva Bhadrapada", "pada": 4}
  },
  "vimshottari_dasha": {
    "current": {
      "mahadasha": "Venus",
      "antardasha": "Mercury",
      "pratyantardasha": "Moon"
    }
  }
};

const LLMTesting = ({ kernelData, bulkKernels = [] }) => {
  const [mode, setMode] = useState('single'); // 'single' or 'bulk'
  const [selectedPersonIndex, setSelectedPersonIndex] = useState(0);
  const [useSampleKernel, setUseSampleKernel] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [provider, setProvider] = useState('openai');
  const [model, setModel] = useState('gpt-4o-mini'); // Uses higher rate limits!
  const [apiKey, setApiKey] = useState('');
  const [documents, setDocuments] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  // Debug: Log props whenever they change
  useEffect(() => {
    console.log('🔍 LLMTesting Props Updated:', {
      bulkKernelsLength: bulkKernels?.length || 0,
      bulkKernelsData: bulkKernels,
      hasKernelData: !!kernelData,
      selectedPersonIndex,
      mode
    });
  }, [bulkKernels, kernelData, selectedPersonIndex, mode]);

  // Model options based on provider
  const modelOptions = {
    openai: [
      { value: 'gpt-4o', label: 'GPT-4o (Best & Fast)', cost: '$2.50/$10 per 1M tokens' },
      { value: 'gpt-4o-mini', label: 'GPT-4o Mini (Fastest)', cost: '$0.15/$0.60 per 1M tokens' },
      { value: 'gpt-4-turbo', label: 'GPT-4 Turbo', cost: '$10/$30 per 1M tokens' },
      { value: 'o1-mini', label: 'O1 Mini (Reasoning)', cost: '$3/$12 per 1M tokens' },
      { value: 'o1', label: 'O1 (Advanced Reasoning)', cost: '$15/$60 per 1M tokens' },
    ],
    anthropic: [
      { value: 'claude-sonnet-4-20250514', label: 'Claude Sonnet 4 (Best)', cost: '$3/$15 per 1M tokens' },
      { value: 'claude-opus-4-20250514', label: 'Claude Opus 4 (Most Capable)', cost: '$15/$75 per 1M tokens' },
      { value: 'claude-3-7-sonnet-20250219', label: 'Claude 3.7 Sonnet', cost: '$3/$15 per 1M tokens' },
      { value: 'claude-3-5-haiku-20241022', label: 'Claude 3.5 Haiku (Fast)', cost: '$0.80/$4 per 1M tokens' },
    ],
    gemini: [
      { value: 'gemini-2.5-flash-preview-04-17', label: 'Gemini 2.5 Flash (Fast)', cost: '$0.075/$0.30 per 1M tokens' },
      { value: 'gemini-2.5-pro-preview-05-06', label: 'Gemini 2.5 Pro (Best)', cost: '$1.25/$5 per 1M tokens' },
      { value: 'gemini-2.0-flash', label: 'Gemini 2.0 Flash', cost: '$0.075/$0.30 per 1M tokens' },
      { value: 'gemini-1.5-pro', label: 'Gemini 1.5 Pro', cost: '$1.25/$5 per 1M tokens' },
    ]
  };

  // Update model when provider changes
  useEffect(() => {
    if (modelOptions[provider] && modelOptions[provider].length > 0) {
      setModel(modelOptions[provider][0].value);
    }
  }, [provider]);

  // Set mode based on available data
  useEffect(() => {
    if (bulkKernels && bulkKernels.length > 0) {
      setMode('bulk');
      setSelectedPersonIndex(0);
    } else if (kernelData) {
      setMode('single');
    }
  }, [kernelData, bulkKernels]);

  const handleDocumentUpload = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => 
      f.name.endsWith('.md') || 
      f.name.endsWith('.txt') || 
      f.name.endsWith('.csv') || 
      f.name.endsWith('.pdf')
    );
    
    if (validFiles.length !== files.length) {
      setError('Only .md, .txt, .csv, and .pdf files are supported');
      setTimeout(() => setError(null), 3000);
    }
    
    if (documents.length + validFiles.length > 8) {
      setError('Maximum 8 documents allowed');
      setTimeout(() => setError(null), 3000);
      return;
    }
    
    setDocuments([...documents, ...validFiles.slice(0, 8 - documents.length)]);
  };

  const removeDocument = (index) => {
    setDocuments(documents.filter((_, i) => i !== index));
  };

  const handleGeneratePredictionStreaming = async () => {
    console.log('🌊 Starting STREAMING mode');
    
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }
    
    // API key is now optional - Universal Key is used if empty
    if (!apiKey.trim()) {
      console.log('🔑 No API key provided, will use Universal Key');
    }

    // Get kernel - Priority: Sample > Bulk > Single Chart
    let currentKernel;
    if (useSampleKernel) {
      console.log('🔮 Using sample kernel: Mandeep Singh');
      currentKernel = SAMPLE_KERNEL;
    } else if (bulkKernels && bulkKernels.length > 0 && bulkKernels[selectedPersonIndex]) {
      currentKernel = bulkKernels[selectedPersonIndex].kernel;
    } else if (kernelData) {
      currentKernel = kernelData;
    }
    
    if (!currentKernel) {
      setError('No kernel data available. Enable sample chart or upload data first.');
      return;
    }

    setIsProcessing(true);
    setError(null);
    setResult({ prediction: '', streaming: true, phases: {} });

    try {
      const formData = new FormData();
      formData.append('prompt', prompt);
      formData.append('kernel_data', JSON.stringify(currentKernel));
      formData.append('provider', provider);
      formData.append('model', model);
      formData.append('api_key', apiKey);
      
      documents.forEach(doc => {
        formData.append('documents', doc);
      });

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';
      const response = await fetch(`${backendUrl}/api/llm/stream-predict`, {
        method: 'POST',
        body: formData,
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let fullPrediction = '';
      let phases = {};

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.trim() && line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              console.log('📥 Received:', data.type);
              
              if (data.type === 'test') {
                console.log('✅ Stream test:', data.message);
              }
              else if (data.type === 'phase_start') {
                console.log(`🚀 Phase ${data.phase} started: ${data.title}`);
                const phaseHeader = `\n\n${'='.repeat(80)}\n🚀 PHASE ${data.phase} — ${data.title}\n${'='.repeat(80)}\n\n`;
                fullPrediction += phaseHeader;
                setResult(prev => ({ ...prev, prediction: fullPrediction }));
              }
              else if (data.type === 'phase_content') {
                console.log(`✅ Phase ${data.phase} content received (${data.content.length} chars)`);
                console.log(`📝 Content preview:`, data.content.substring(0, 100));
                fullPrediction += data.content + '\n\n';
                phases[data.phase] = data.content;
                console.log(`📊 Total prediction length now: ${fullPrediction.length} chars`);
                setResult(prev => ({ 
                  ...prev, 
                  prediction: fullPrediction, 
                  phases,
                  streaming: true 
                }));
                console.log(`✅ State updated with ${fullPrediction.length} chars`);
              }
              else if (data.type === 'phase_complete') {
                console.log(`✓ Phase ${data.phase} complete`);
              }
              else if (data.type === 'complete') {
                console.log('🎉 All phases complete!');
                setResult(prev => ({ ...prev, streaming: false }));
                setIsProcessing(false);
              }
              else if (data.type === 'error') {
                console.error('❌ Stream error:', data.message);
                setError(data.message);
                setIsProcessing(false);
              }
            } catch (e) {
              console.error('Parse error:', e, 'Line:', line);
            }
          }
        }
      }
    } catch (err) {
      console.error('Streaming error:', err);
      setError(err.message || 'Failed to generate prediction');
      setIsProcessing(false);
    }
  };

  const handleGeneratePrediction = async (mode = 'single') => {
    console.log('🚀 handleGeneratePrediction called with mode:', mode);
    console.log('📊 Current state:', {
      bulkKernelsLength: bulkKernels?.length || 0,
      selectedPersonIndex,
      hasKernelData: !!kernelData,
      promptLength: prompt.trim().length,
      apiKeyLength: apiKey.trim().length
    });

    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }
    
    // API key is now optional - Universal Key is used if empty
    if (!apiKey.trim()) {
      console.log('🔑 No API key provided, will use Universal Key');
    }

    // Get the kernel to use
    // Priority: Sample > Bulk > Single Chart
    let currentKernel;
    
    if (useSampleKernel) {
      console.log('🔮 Using sample kernel: Mandeep Singh');
      currentKernel = SAMPLE_KERNEL;
    } else if (bulkKernels && bulkKernels.length > 0 && bulkKernels[selectedPersonIndex]) {
      currentKernel = bulkKernels[selectedPersonIndex].kernel;
    } else if (kernelData) {
      currentKernel = kernelData;
    }
    
    if (!currentKernel) {
      setError('No kernel data available. Enable sample chart or upload data first.');
      return;
    }
    
    console.log('✅ Kernel selected, proceeding with API call');

    setIsProcessing(true);
    setError(null);
    setResult(null);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';
      let endpoint = '/api/llm/predict';
      
      // Determine endpoint
      if (mode === 'parallel6') {
        endpoint = '/api/llm/single-optimized';  // FASTEST: 1 call, all 6 phases, no rate limits!
      } else if (mode === 'ultra') {
        endpoint = '/api/llm/ultra-complete';
      } else if (mode === 'multi') {
        endpoint = '/api/llm/predict-multi-stage';
      }
      
      // Build form data based on endpoint
      const formData = new FormData();
      formData.append('kernel_data', JSON.stringify(currentKernel));
      formData.append('provider', provider);
      formData.append('model', model);
      formData.append('api_key', apiKey);
      
      // Production endpoint doesn't need prompt/documents (canon pre-loaded)
      if (mode !== 'parallel6') {
        formData.append('prompt', prompt);
        documents.forEach(doc => {
          formData.append('documents', doc);
        });
      }
      
      const response = await fetch(`${backendUrl}${endpoint}`, {
        method: 'POST',
        body: formData,
      });

      // Check response status BEFORE reading body
      if (!response.ok) {
        let errorMsg = 'Prediction failed';
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorMsg;
        } catch (e) {
          errorMsg = `Server error: ${response.status}`;
        }
        throw new Error(errorMsg);
      }

      // Only read body if response is OK
      const data = await response.json();
      setResult(data);

    } catch (err) {
      setError(err.message || 'Failed to generate prediction');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-purple-900/50 to-blue-900/30 backdrop-blur-xl rounded-2xl border border-purple-500/20 p-6">
        <div className="flex items-center gap-3 mb-2">
          <Brain className="w-8 h-8 text-purple-400" />
          <h2 className="text-2xl font-bold text-white">LLM Prediction Testing</h2>
        </div>
        <p className="text-purple-200 text-sm">
          Generate AI-powered astrological predictions using your own LLM API keys and custom prompts.
        </p>
        <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-3 mt-4">
          <p className="text-sm text-green-300 font-medium">
            🔒 100% Private & Secure
          </p>
          <p className="text-xs text-gray-300 mt-1">
            • Your documents and kernels are processed in real-time and never stored<br/>
            • With Universal Key: Your data is NOT used for AI training<br/>
            • All API calls are ephemeral - no logs, no retention<br/>
            • Your prompts and outputs belong to you only
          </p>
        </div>
      </div>

      {/* Sample Kernel Toggle */}
      <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 backdrop-blur-xl rounded-xl border border-cyan-500/20 p-4">
        <div className="flex items-center justify-between">
          <div>
            <label className="text-white font-medium flex items-center gap-2">
              <Star className="w-5 h-5 text-yellow-400" />
              Use Sample Chart (Mandeep Singh)
            </label>
            <p className="text-xs text-gray-400 mt-1">
              Pre-loaded optimized kernel for instant testing • Leo Lagna • Sun Exalted in 9th
            </p>
          </div>
          <button
            onClick={() => setUseSampleKernel(!useSampleKernel)}
            className={`relative w-14 h-8 rounded-full transition-colors ${
              useSampleKernel ? 'bg-green-500' : 'bg-gray-600'
            }`}
          >
            <div
              className={`absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform ${
                useSampleKernel ? 'translate-x-6' : 'translate-x-0'
              }`}
            />
          </button>
        </div>
        {useSampleKernel && (
          <div className="mt-3 text-xs text-cyan-300 bg-cyan-900/20 rounded-lg p-2">
            ✨ Sample chart active: Mandeep Singh (DOB: 14-May-1997, 12:17, Dehradun, India)
          </div>
        )}
      </div>

      {/* Mode Selection (if bulk kernels available) */}
      {!useSampleKernel && bulkKernels && bulkKernels.length > 0 ? (
        <div className="bg-gray-900/50 backdrop-blur-xl rounded-xl border border-white/10 p-4">
          <label className="text-white font-medium mb-2 block">
            Select Person 
            <span className="text-xs text-green-400 ml-2">
              ({bulkKernels.length} loaded ✓)
            </span>
          </label>
          <select
            value={selectedPersonIndex}
            onChange={(e) => {
              const newIndex = parseInt(e.target.value);
              console.log('🔄 Changing person to index:', newIndex, bulkKernels[newIndex]);
              setSelectedPersonIndex(newIndex);
            }}
            className="w-full bg-gray-800/50 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {bulkKernels.map((person, index) => (
              <option key={index} value={index}>
                {person.name || `Person ${index + 1}`} - {person.date} {person.time}
              </option>
            ))}
          </select>
          
          {/* Debug info */}
          <div className="mt-2 text-xs text-gray-400">
            Selected: {bulkKernels[selectedPersonIndex]?.name} 
            {bulkKernels[selectedPersonIndex]?.kernel ? ' ✓ Kernel OK' : ' ✗ No Kernel!'}
          </div>
        </div>
      ) : !useSampleKernel && (
        <div className="bg-yellow-900/50 border border-yellow-500/50 rounded-xl p-4">
          <p className="text-yellow-200 text-sm">
            💡 Enable "Sample Chart" above for instant testing, or upload CSV from "Bulk Upload" tab
          </p>
        </div>
      )}

      {/* LLM Configuration */}
      <div className="bg-gray-900/50 backdrop-blur-xl rounded-xl border border-white/10 p-6 space-y-4">
        <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-yellow-400" />
          LLM Configuration
        </h3>

        {/* Provider Selection */}
        <div>
          <label className="text-white font-medium mb-2 block">Provider</label>
          <div className="grid grid-cols-3 gap-3">
            {['openai', 'anthropic', 'gemini'].map((p) => (
              <button
                key={p}
                onClick={() => setProvider(p)}
                className={`px-4 py-3 rounded-lg font-medium transition-all ${
                  provider === p
                    ? 'bg-purple-600 text-white border-2 border-purple-400'
                    : 'bg-gray-800/50 text-gray-300 border border-white/20 hover:bg-gray-700/50'
                }`}
              >
                {p.charAt(0).toUpperCase() + p.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Model Selection */}
        <div>
          <label className="text-white font-medium mb-2 block">Model</label>
          <select
            value={model}
            onChange={(e) => setModel(e.target.value)}
            className="w-full bg-gray-800/50 border border-white/20 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            {modelOptions[provider].map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label} - {opt.cost}
              </option>
            ))}
          </select>
        </div>

        {/* API Key */}
        <div>
          <label className="text-white font-medium mb-2 block">
            API Key
            <span className="text-xs text-green-400 ml-2">✨ Optional - Leave empty to use Universal Key</span>
          </label>
          <div className="relative">
            <input
              type="text"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder="Leave empty for Universal Key"
              className="w-full bg-gray-800/50 border border-white/20 rounded-lg px-4 py-3 pr-20 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            {apiKey && (
              <button
                onClick={() => setApiKey('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Custom Prompt */}
      <div className="bg-gray-900/50 backdrop-blur-xl rounded-xl border border-white/10 p-6">
        <label className="text-white font-medium mb-2 block">Your Prompt</label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="Enter your custom prompt here... (e.g., 'Analyze career prospects for the next 5 years based on the planetary positions and dashas')"
          rows={6}
          className="w-full bg-gray-800/50 border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
        />
      </div>

      {/* Document Upload */}
      <div className="bg-gray-900/50 backdrop-blur-xl rounded-xl border border-white/10 p-6">
        <label className="text-white font-medium mb-2 block flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Reference Documents (Optional - Max 8)
        </label>
        <p className="text-gray-400 text-sm mb-3">
          Upload astrology rules, reference texts, or knowledge base documents (.md, .txt, .csv, .pdf)
        </p>
        
        <label className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-800/50 border-2 border-dashed border-white/20 rounded-lg cursor-pointer hover:bg-gray-700/50 hover:border-purple-500/50 transition-all">
          <Upload className="w-5 h-5 text-purple-400" />
          <span className="text-white">Choose Files</span>
          <input
            type="file"
            multiple
            accept=".md,.txt,.csv,.pdf"
            onChange={handleDocumentUpload}
            className="hidden"
          />
        </label>

        {/* Uploaded Documents */}
        {documents.length > 0 && (
          <div className="mt-4 space-y-2">
            {documents.map((doc, index) => (
              <div
                key={index}
                className="flex items-center justify-between bg-gray-800/50 border border-white/10 rounded-lg px-4 py-2"
              >
                <div className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-purple-400" />
                  <span className="text-white text-sm">{doc.name}</span>
                  <span className="text-gray-400 text-xs">
                    ({(doc.size / 1024).toFixed(1)} KB)
                  </span>
                </div>
                <button
                  onClick={() => removeDocument(index)}
                  className="text-red-400 hover:text-red-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Generate Buttons */}
      <div className="space-y-3">
        <button
          onClick={() => handleGeneratePrediction('parallel6')}
          disabled={isProcessing || !prompt.trim()}
          className="w-full bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 hover:from-green-700 hover:via-emerald-700 hover:to-teal-700 text-white font-bold py-6 px-6 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg shadow-green-500/50 border-2 border-yellow-400"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              ⚡ Running 6 Parallel Calls...
            </>
          ) : (
            <>
              <Zap className="w-6 h-6" />
              🚀 OPTIMIZED (All 6 Phases - 5-10s!)
            </>
          )}
        </button>
        
        <button
          onClick={() => handleGeneratePrediction('ultra')}
          disabled={isProcessing || !prompt.trim()}
          className="w-full bg-gradient-to-r from-yellow-600 to-red-600 hover:from-yellow-700 hover:to-red-700 text-white font-bold py-5 px-6 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg shadow-yellow-500/50"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              ⚡ Generating All 6 Phases...
            </>
          ) : (
            <>
              <Zap className="w-6 h-6" />
              ⚡ ULTRA-FAST (Single Call - Compressed)
            </>
          )}
        </button>
        
        <button
          onClick={handleGeneratePredictionStreaming}
          disabled={isProcessing || !prompt.trim()}
          className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg shadow-cyan-500/25"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              🌊 Streaming...
            </>
          ) : (
            <>
              <Sparkles className="w-5 h-5" />
              🌊 Streaming Mode (Progressive)
            </>
          )}
        </button>
        
        <button
          onClick={() => handleGeneratePrediction('multi')}
          disabled={isProcessing || !prompt.trim()}
          className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white font-semibold py-4 px-6 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg shadow-green-500/25"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Generating Deep Analysis...
            </>
          ) : (
            <>
              <Zap className="w-5 h-5" />
              🔥 Deep Analysis (30-40 sec - Sequential)
            </>
          )}
        </button>
        
        <button
          onClick={() => handleGeneratePrediction('single')}
          disabled={isProcessing || !prompt.trim()}
          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg shadow-purple-500/25"
        >
          {isProcessing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Generating Quick...
            </>
          ) : (
            <>
              <Zap className="w-5 h-5" />
              Quick (10-20 sec - Single Call)
            </>
          )}
        </button>
        
        <p className="text-xs text-center text-gray-400">
          🚀 OPTIMIZED: Single call with all 6 phases - Full accuracy - No rate limits (5-10s) ⭐ RECOMMENDED<br/>
          ⚡ ULTRA-FAST: 1 call with compressed context - faster but shorter responses<br/>
          🌊 STREAMING: Phases appear progressively (infrastructure limited)<br/>
          🔥 DEEP: 4-stage sequential analysis - 30-60 seconds<br/>
          💨 QUICK: Basic single analysis - 10-20 seconds
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-900/50 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-200 font-medium">Error</p>
            <p className="text-red-300 text-sm mt-1">{error}</p>
          </div>
        </div>
      )}

      {/* Result Display */}
      {result && (
        <div className="space-y-4">
          {/* Success Header */}
          <div className="bg-green-900/50 border border-green-500/50 rounded-xl p-4 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <p className="text-green-200 font-medium">Prediction Generated Successfully!</p>
          </div>

          {/* Cost Information */}
          {result.model_info && result.tokens && result.cost && (
            <div className="bg-gradient-to-br from-yellow-900/30 to-orange-900/30 backdrop-blur-xl rounded-xl border border-yellow-500/20 p-6">
              <div className="flex items-center gap-2 mb-3">
                <DollarSign className="w-5 h-5 text-yellow-400" />
                <h3 className="text-white font-semibold">Cost Breakdown</h3>
              </div>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-400">Provider</p>
                  <p className="text-white font-medium">{result.model_info?.provider || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-400">Model</p>
                  <p className="text-white font-medium">{result.model_info?.model || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-400">Input Tokens</p>
                  <p className="text-white font-medium">{result.tokens?.input?.toLocaleString() || '0'}</p>
                </div>
                <div>
                  <p className="text-gray-400">Output Tokens</p>
                  <p className="text-white font-medium">{result.tokens?.output?.toLocaleString() || '0'}</p>
                </div>
                <div className="col-span-2 pt-2 border-t border-yellow-500/20">
                  <p className="text-gray-400">Total Cost</p>
                  <p className="text-yellow-300 font-bold text-2xl">{result.cost?.formatted || '$0.00'}</p>
                </div>
              </div>
            </div>
          )}

          {/* Prediction Output */}
          <div className="bg-gray-900/50 backdrop-blur-xl rounded-xl border border-white/10 p-6">
            <h3 className="text-white font-semibold text-lg mb-4 flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-400" />
              AI Prediction {result.multi_stage_analysis && '(4-Stage Deep Analysis)'}
              {result.streaming && (
                <span className="ml-3 text-sm text-yellow-400 animate-pulse">
                  🌊 Streaming in real-time...
                </span>
              )}
            </h3>
            <div className="prose prose-invert max-w-none">
              {result.prediction && result.prediction.length > 0 ? (
                <div className="text-gray-200 whitespace-pre-wrap leading-relaxed">
                  {result.prediction}
                </div>
              ) : (
                <div className="text-gray-400 italic">
                  {result.streaming ? 'Waiting for Phase 1...' : 'No content yet'}
                </div>
              )}
              {result.streaming && (
                <div className="mt-4 flex items-center gap-2 text-yellow-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Generating next phase... (Length: {result.prediction?.length || 0} chars)</span>
                </div>
              )}
            </div>
            
            {/* Show stage details if multi-stage */}
            {result.multi_stage_analysis && (
              <details className="mt-6">
                <summary className="cursor-pointer text-purple-300 font-medium">
                  View Individual Stage Analyses
                </summary>
                <div className="mt-4 space-y-4">
                  <div className="bg-gray-800/50 p-4 rounded-lg">
                    <h4 className="text-green-400 font-semibold mb-2">Stage 1: Birth + Childhood</h4>
                    <p className="text-gray-300 text-sm whitespace-pre-wrap">{result.multi_stage_analysis.stage_1_birth_childhood}</p>
                  </div>
                  <div className="bg-gray-800/50 p-4 rounded-lg">
                    <h4 className="text-blue-400 font-semibold mb-2">Stage 2: Disease Timeline</h4>
                    <p className="text-gray-300 text-sm whitespace-pre-wrap">{result.multi_stage_analysis.stage_2_disease_timeline}</p>
                  </div>
                  <div className="bg-gray-800/50 p-4 rounded-lg">
                    <h4 className="text-yellow-400 font-semibold mb-2">Stage 3: Current + Recommendations</h4>
                    <p className="text-gray-300 text-sm whitespace-pre-wrap">{result.multi_stage_analysis.stage_3_current_recommendations}</p>
                  </div>
                </div>
              </details>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LLMTesting;
